import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Divider,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import SubHeader from "../../../common/SubHeader";
import { DataGrid } from "@mui/x-data-grid";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CreateUser from "./CreateUser";
import EditUser from "./EditUser";
import Transition from "../../../common/Transition";
import Notification from "../../../ui/Notification";
import RenderStatus from "../../../ui/RenderStatus";
import CustomToolbar from "../../../ui/CustomToolBar";
import Route from "../../../routes/Route";
import { useCommon } from "../../../contexts/CommonContext";

const User = () => {
  const { isMdUp } = useCommon();
  // init states
  const [users, setUsers] = useState([]);
  const [createUser, setCreateUser] = useState(false);
  const [editUser, setEditUser] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [deleteUser, setDeleteUser] = useState(false);
  const [userId, setUserId] = useState("");
  const [message, setMessage] = React.useState("");
  const [openNotification, setOpenNotification] = useState(false);
  const [severity, setSeverity] = useState("info");
  // handlers
  const editHandle = (param) => {
    setUserDetails(param?.row);
    setEditUser(true);
  };
  const deleteHandle = (param) => {
    setUserId(param?.id);
    setDeleteUser(true);
  };
  const userColumns = [
    {
      field: "sl",
      headerName: "Sl. No",
      flex: isMdUp ? 40 : undefined,
      width: isMdUp ? undefined : 40,
      valueGetter: (params) => params.row.sl,
    },
    {
      field: "name",
      headerName: "Name",
      flex: isMdUp ? 180 : undefined,
      width: isMdUp ? undefined : 180,
    },
    {
      field: "empId",
      headerName: "Employee ID",
      flex: isMdUp ? 90 : undefined,
      width: isMdUp ? undefined : 150,
    },
    {
      field: "email",
      headerName: "Email",
      flex: isMdUp ? 240 : undefined,
      width: isMdUp ? undefined : 240,
    },
    {
      field: "designation ",
      headerName: "Designation",
      flex: isMdUp ? 140 : undefined,
      width: isMdUp ? undefined : 140,
      valueGetter: (params) => params.row.Designation?.title || "N/A",
    },
    {
      field: "contact",
      headerName: "Contact No.",
      flex: isMdUp ? 100 : undefined,
      width: isMdUp ? undefined : 140,
    },
    {
      field: "isAdmin",
      headerName: "Admin",
      flex: isMdUp ? 80 : undefined,
      width: isMdUp ? undefined : 110,
      renderCell: (params) => (
        <RenderStatus status={params?.row?.isAdmin === true ? "Yes" : "No"} />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: isMdUp ? 90 : undefined,
      width: isMdUp ? undefined : 110,
      renderCell: (params) => (
        <div>
          <IconButton
            aria-label="edit"
            size="small"
            onClick={() => editHandle(params)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            aria-label="delete"
            size="small"
            onClick={() => deleteHandle(params)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ),
    },
  ];
  const createUserHandle = () => {
    setCreateUser(true);
  };
  const token = localStorage.getItem("token");
  const fetchUsers = async () => {
    const res = await Route("GET", "/users", token, null, null);
    if (res?.status === 200) {
      setUsers(res?.data?.users);
    }
  };
  useEffect(() => {
    fetchUsers();
  }, []);
  const confirmDeleteHandler = async () => {
    const res = await Route("PUT", `/users/delete-users`, token, null, userId);
    if (res?.status === 200) {
      setDeleteUser(false);
      setMessage(res?.data?.message);
      fetchUsers();
      setOpenNotification(true);
    } else {
      setMessage(res?.data?.message);
      setOpenNotification(true);
    }
  };
  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={4} alignItems="center" sx={{ px: 2 }}>
          <SubHeader text="Users" />
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              endIcon={<PersonAddIcon />}
              onClick={createUserHandle}
            >
              Create User
            </Button>
          </Grid>
          <Grid item container alignItems="center" xs={12}>
            <div style={{ height: "auto", width: "100%" }}>
              <DataGrid
                rows={users?.map((row, index) => ({
                  ...row,
                  sl: index + 1,
                }))}
                columns={userColumns}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 5 },
                  },
                }}
                pageSizeOptions={[5, 10]}
                slots={{ toolbar: CustomToolbar }}
              />
            </div>
          </Grid>
        </Grid>
      </Box>
      {createUser ? (
        <CreateUser
          open={createUser}
          setOpen={setCreateUser}
          setOpenNotification={setOpenNotification}
          setMessage={setMessage}
          fetchUsers={fetchUsers}
        />
      ) : null}
      {editUser ? (
        <EditUser
          details={userDetails}
          open={editUser}
          setOpen={setEditUser}
          setOpenNotification={setOpenNotification}
          setSeverity={setSeverity}
          setMessage={setMessage}
          fetchUsers={fetchUsers}
        />
      ) : null}
      {deleteUser ? (
        <Dialog
          open={deleteUser}
          onClose={() => setDeleteUser(false)}
          TransitionComponent={Transition}
        >
          <DialogContent>
            <Typography variant="h6">Confirmation</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1">
              Are you sure you want to delete this user?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ mb: 2, mx: 2 }}>
            <Button
              onClick={confirmDeleteHandler}
              variant="contained"
              autoFocus
              size="small"
            >
              Confirm
            </Button>
            <Button
              onClick={() => setDeleteUser(false)}
              variant="outlined"
              color="error"
              size="small"
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
      {openNotification && (
        <Notification
          open={openNotification}
          setOpen={setOpenNotification}
          severity={severity}
          message={message}
        />
      )}
    </>
  );
};

export default User;
